import { Attachments } from '../attachments/Attachments'
// A plan's comments (today's components/Comments.tsx, ported to the store's
// plan comments): threads one level deep, a composer, replies, and editing
// one's own. From four threads the list is an index: a thread is open, or
// folded to one row with its time, its author and its first words; the last
// thread and the one with the newest comment start open. A comment the import
// brought from today's board shows as its author's there, at its time, marked
// imported, and no one edits it. Deleting a comment is not the server's.
// `#comment-<id>` opens that comment's thread and scrolls to it.
import { useEffect, useId, useLayoutEffect, useMemo, useState, type FormEvent, type KeyboardEvent } from 'react'
import { useLocation } from 'react-router-dom'
import type { Actor, Me, PlanComment } from '../api/types'
import { ChevronRightIcon } from '../components/Icons'
import { SectionHeader } from '../components/layout'
import { clockTime, exactTime, Time } from '../components/Time'
import { confirmDiscard, useUnsavedChanges } from '../components/unsaved'
import { Markdown } from '../markdown/Markdown'
import { plainSnippet } from '../search/snippet'
import { senderName } from '../sessions/words'
import { useBoardState, useStore } from '../store/context'
import { cx } from '../transcript/bits'
import { useWorkspaceDraft } from '../workspace/drafts'
import { useTabScope } from '../workspace/context'
import { commentWords as w } from './words'

export interface Thread {
  comment: PlanComment
  replies: PlanComment[]
}

const byCreated = (a: PlanComment, b: PlanComment) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : a.id < b.id ? -1 : 1)

/** Top-level comments in order, each with its replies. */
export function buildThreads(comments: readonly PlanComment[]): Thread[] {
  const ids = new Set(comments.map((c) => c.id))
  const replies = new Map<string, PlanComment[]>()
  const top: PlanComment[] = []
  for (const c of comments) {
    if (c.parent && ids.has(c.parent)) replies.set(c.parent, [...(replies.get(c.parent) ?? []), c])
    else top.push(c)
  }
  return top.sort(byCreated).map((comment) => ({ comment, replies: (replies.get(comment.id) ?? []).sort(byCreated) }))
}

/** From this many threads the list is an index. */
export const INDEX_FROM = 4

type Fold = 'open' | 'folded' | 'row'

function lastWritten({ comment, replies }: Thread): string {
  return replies.reduce((at, reply) => (reply.created_at > at ? reply.created_at : at), comment.created_at)
}

/** The threads an index shows open without a click: the last one, and the one that holds the newest comment when that is another. */
export function openByDefault(threads: readonly Thread[]): string[] {
  const last = threads.at(-1)
  if (!last) return []
  let newest = last
  for (const thread of threads) if (lastWritten(thread) > lastWritten(newest)) newest = thread
  return newest === last ? [last.comment.id] : [last.comment.id, newest.comment.id]
}

const FIRST_WORDS_MAX = 200

/** A comment's first words as one line of text, `…` where it was cut. */
export function firstWords(body: string): string {
  const text = plainSnippet(body.slice(0, 4 * FIRST_WORDS_MAX))
  if (text.length <= FIRST_WORDS_MAX) return text
  const space = text.lastIndexOf(' ', FIRST_WORDS_MAX)
  return `${text.slice(0, space > 0 ? space : FIRST_WORDS_MAX)}…`
}

export function commentAnchor(id: string): string {
  return `comment-${id}`
}

function commentIdFromHash(hash: string): string | null {
  if (!hash.startsWith('#comment-') || hash.length === '#comment-'.length) return null
  const raw = hash.slice('#comment-'.length)
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

/** Who wrote a comment, as it shows: today's author for an imported one. */
function authorName(comment: PlanComment, me: Me | null): string {
  return comment.source_author ?? senderName(comment.author, me)
}

function sameHolder(holder: Actor, actor: Actor | undefined): boolean {
  if (!actor || holder.kind !== actor.kind) return false
  if (holder.kind === 'person' && actor.kind === 'person') return holder.member === actor.member
  if (holder.kind === 'agent' && actor.kind === 'agent') return holder.session === actor.session
  return holder.kind === 'poulpe'
}

export function PlanComments({ planId, comments }: { planId: string; comments: readonly PlanComment[] }) {
  const scope = useTabScope()
  const { me } = useBoardState()
  const { hash } = useLocation()
  const listId = useId()
  const threads = useMemo(() => buildThreads(comments), [comments])
  const isIndex = threads.length >= INDEX_FROM
  const targetId = commentIdFromHash(hash)
  const linked = threads.find(({ comment, replies }) => comment.id === targetId || replies.some((reply) => reply.id === targetId))
  const linkedId = linked?.comment.id ?? null

  const byDefault = useMemo(() => {
    const ids = new Set(isIndex ? openByDefault(threads) : threads.map((thread) => thread.comment.id))
    if (linkedId !== null) ids.add(linkedId)
    return ids
  }, [threads, isIndex, linkedId])
  const [folds, setFolds] = useState<ReadonlyMap<string, Fold>>(() => new Map(threads.map(({ comment }) => [comment.id, byDefault.has(comment.id) ? 'open' : 'row'])))
  const isOpen = (id: string) => {
    if (!isIndex) return true
    const fold = folds.get(id)
    return fold === undefined ? byDefault.has(id) : fold === 'open'
  }
  // A thread that has just come is recorded as it starts, before the page is painted.
  const unrecorded = threads.flatMap(({ comment: { id } }): [string, Fold][] => (folds.has(id) ? [] : [[id, isOpen(id) ? 'open' : 'row']]))
  useLayoutEffect(() => {
    if (unrecorded.length > 0) setFolds((before) => new Map([...before, ...unrecorded]))
  })

  // A new link opens an existing folded thread too, as when searching
  // from this plan's own page.
  useLayoutEffect(() => {
    if (linkedId !== null) setFolds((before) => before.get(linkedId) === 'open' ? before : new Map(before).set(linkedId, 'open'))
  }, [linkedId, hash])
  const linkedOpen = linkedId !== null && isOpen(linkedId)

  // A link to a comment scrolls to it once it is on the page.
  useEffect(() => {
    if (!targetId || !linkedId) return
    const element = [...(scope?.surface() ?? document).querySelectorAll<HTMLElement>('[id]')].find((e) => e.id === commentAnchor(targetId))
    element?.scrollIntoView?.({ block: 'center' })
  }, [targetId, linkedId, linkedOpen, scope])

  const setOpen = (id: string, open: boolean) => setFolds((before) => new Map(before).set(id, open ? 'open' : 'folded'))
  const allOpen = threads.every((thread) => isOpen(thread.comment.id))
  const openAll = () => setFolds(new Map<string, Fold>(threads.map(({ comment }) => [comment.id, 'open'])))
  const foldEarlier = () => setFolds(new Map<string, Fold>(threads.map(({ comment }) => [comment.id, byDefault.has(comment.id) ? 'open' : 'folded'])))

  const item = (comment: PlanComment) => <CommentItem comment={comment} me={me} isTarget={comment.id === targetId} />

  return (
    <section className="section comments">
      <SectionHeader
        title={w.title}
        count={comments.length}
        actions={
          isIndex && (
            <button type="button" className="button button-quiet button-small" onClick={allOpen ? foldEarlier : openAll}>
              {allOpen ? w.foldEarlier : w.openAll}
            </button>
          )
        }
      />
      {threads.length > 0 ? (
        <ol className="comment-list">
          {threads.map((thread) => {
            const id = thread.comment.id
            const open = isOpen(id)
            const onPage = open || folds.get(id) === 'folded'
            const bodyId = `${listId}-${id}`
            return (
              <li key={id} className={cx('comment-thread', isIndex && 'is-index', isIndex && !open && 'is-folded')}>
                {isIndex && (
                  <button
                    type="button"
                    className="comment-toggle"
                    aria-expanded={open}
                    aria-controls={onPage ? bodyId : undefined}
                    aria-label={open ? w.fold : undefined}
                    title={open ? w.fold : undefined}
                    onClick={() => setOpen(id, !open)}
                  >
                    <ChevronRightIcon size={12} />
                    {!open && <ThreadLine thread={thread} me={me} />}
                  </button>
                )}
                {onPage && (
                  <div className="comment-thread-body" id={isIndex ? bodyId : undefined} hidden={!open}>
                    {item(thread.comment)}
                    {thread.replies.length > 0 && (
                      <ol className="comment-replies">
                        {thread.replies.map((reply) => (
                          <li key={reply.id}>{item(reply)}</li>
                        ))}
                      </ol>
                    )}
                    <ReplyBox planId={planId} parentId={id} />
                  </div>
                )}
              </li>
            )
          })}
        </ol>
      ) : (
        <p className="muted">{w.none}</p>
      )}
      <Composer planId={planId} parentId={null} placeholder={w.leave} />
    </section>
  )
}

function ThreadLine({ thread, me }: { thread: Thread; me: Me | null }) {
  const { comment, replies } = thread
  return (
    <>
      <time dateTime={comment.created_at} title={exactTime(comment.created_at)}>
        {clockTime(comment.created_at, Date.now())}
      </time>{' '}
      <span className="comment-toggle-author">{authorName(comment, me)}</span>{' '}
      <span className="comment-toggle-words">{firstWords(comment.body)}</span>
      {replies.length > 0 && (
        <>
          {' '}
          <span className="comment-toggle-replies">{w.replies(replies.length)}</span>
        </>
      )}
    </>
  )
}

function CommentItem({ comment, me, isTarget }: { comment: PlanComment; me: Me | null; isTarget: boolean }) {
  const store = useStore()
  const [edit, setEdit] = useWorkspaceDraft<string | null>(`plan-comment:${comment.id}`, null,
    (value): value is string | null => value === null || typeof value === 'string',
    (value) => value !== null && value.trim() !== comment.body.trim())
  const editing = edit !== null
  const draft = edit ?? comment.body
  const [saving, setSaving] = useState(false)
  const dirty = editing && draft.trim() !== comment.body.trim()
  useUnsavedChanges(dirty)
  const imported = comment.source_author !== null
  // An imported comment is its author's on today's board: no one edits it here.
  const own = !imported && sameHolder(comment.author, me?.actor)

  const save = async () => {
    const body = draft.trim()
    if (!body || saving) return
    if (body === comment.body) {
      setEdit(null)
      return
    }
    setSaving(true)
    const saved = await store.editPlanComment(comment, body)
    setSaving(false)
    if (saved) setEdit(null)
  }
  const cancel = () => {
    if (saving) return
    if (dirty && !confirmDiscard()) return
    setEdit(null)
  }

  return (
    <article className={cx('comment', isTarget && 'is-target')} id={commentAnchor(comment.id)}>
      <header className="comment-header">
        <span className="comment-author">{authorName(comment, me)}</span>
        {imported && <span className="muted comment-imported">{w.imported}</span>}
        <Time iso={comment.created_at} />
        {comment.edited_at && (
          <span className="muted" title={w.editedAt(exactTime(comment.edited_at))}>
            {w.edited}
          </span>
        )}
        {own && !editing && (
          <button type="button" className="button button-quiet button-small" onClick={() => setEdit(comment.body)}>
            {w.edit}
          </button>
        )}
      </header>
      {editing ? (
        <div className="comment-edit">
          <textarea
            className="textarea"
            value={draft}
            onChange={(e) => setEdit(e.target.value)}
            onKeyDown={(e) => submitOnModEnter(e, () => void save(), cancel)}
            aria-label={w.editComment}
            rows={Math.min(12, Math.max(3, draft.split('\n').length + 1))}
            autoFocus
          />
          <div className="form-actions">
            <button type="button" className="button" onClick={cancel} disabled={saving}>
              {w.cancel}
            </button>
            <button type="button" className="button button-primary" onClick={() => void save()} disabled={saving || !draft.trim()}>
              {saving ? w.saving : w.save}
            </button>
          </div>
        </div>
      ) : (
        <div className="comment-body">
          <Markdown source={comment.body} />
          <Attachments target={{ plan: comment.plan, comment: comment.id }} author={comment.author} readOnly={imported} />
        </div>
      )}
    </article>
  )
}

function submitOnModEnter(e: KeyboardEvent, submit: () => void, cancel?: () => void) {
  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
    e.preventDefault()
    submit()
  } else if (e.key === 'Escape' && cancel) {
    e.preventDefault()
    e.stopPropagation()
    cancel()
  }
}

function ReplyBox({ planId, parentId }: { planId: string; parentId: string }) {
  const [open, setOpen] = useWorkspaceDraft(`plan-reply-open:${parentId}`, false, undefined, () => false)
  if (!open) {
    return (
      <button type="button" className="button button-quiet button-small comment-reply-toggle" onClick={() => setOpen(true)}>
        {w.reply}
      </button>
    )
  }
  return <Composer planId={planId} parentId={parentId} onDone={() => setOpen(false)} placeholder={w.reply} autoFocus />
}

function Composer({ planId, parentId, onDone, placeholder, autoFocus = false }: {
  planId: string
  parentId: string | null
  /** A reply's composer closes once its reply is sent, or given up. */
  onDone?: () => void
  placeholder: string
  autoFocus?: boolean
}) {
  const store = useStore()
  const [body, setBody] = useWorkspaceDraft(`plan-comment-new:${planId}:${parentId ?? ''}`, '', undefined, (value) => value.trim() !== '')
  const [sending, setSending] = useState(false)
  const dirty = body.trim() !== ''
  useUnsavedChanges(dirty)

  const submit = async (e?: FormEvent) => {
    e?.preventDefault()
    const text = body.trim()
    if (!text || sending) return
    setSending(true)
    // Once it is kept on this device: the store sends it, and says so if the server refuses it.
    const kept = await store.addPlanComment(planId, text, parentId ?? undefined)
    setSending(false)
    if (kept) {
      setBody('')
      onDone?.()
    }
  }
  const cancel = onDone
    ? () => {
        if (sending) return
        if (dirty && !confirmDiscard()) return
        onDone()
      }
    : undefined

  return (
    <form className={parentId ? 'composer composer-reply' : 'composer'} onSubmit={submit}>
      <textarea
        className="textarea"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => submitOnModEnter(e, () => void submit(), cancel)}
        placeholder={w.hint(placeholder)}
        aria-label={placeholder}
        rows={parentId ? 2 : 3}
        autoFocus={autoFocus}
      />
      <div className="form-actions">
        {cancel && (
          <button type="button" className="button" onClick={cancel} disabled={sending}>
            {w.cancel}
          </button>
        )}
        <button type="submit" className="button button-primary" disabled={sending || !dirty}>
          {parentId ? w.reply : w.comment}
        </button>
      </div>
    </form>
  )
}
